import {
  WordGroup,
  WordGroupBiosResponse,
  WordGroupVideosResponse,
  PublicFeed,
  PublicFeedsResponse,
  PublicFeedVideosResponse,
} from './types'

/**
 * Client for Word Groups API
 * Used to fetch word groups and matching user bios
 * Note: These endpoints are public (read-only). Admin operations require auth.
 */
export class WordGroupsApiClient {
  private baseUrl = 'https://tap.batesky.app'

  /**
   * Get all word groups (public endpoint)
   */
  async getWordGroups(): Promise<WordGroup[]> {
    const url = `${this.baseUrl}/api/word-groups`
    const response = await this.fetch<{wordGroups: WordGroup[]}>(url)
    return response.wordGroups
  }

  /**
   * Get users whose bios match words in the specified word group (public endpoint)
   */
  async getWordGroupBios({
    id,
    limit = 50,
    cursor,
  }: {
    id: number
    limit?: number
    cursor?: string
  }): Promise<WordGroupBiosResponse> {
    const params = new URLSearchParams({limit: String(limit)})
    if (cursor) {
      params.set('cursor', cursor)
    }
    const url = `${this.baseUrl}/api/word-groups/${id}/bios?${params.toString()}`
    return this.fetch(url)
  }

  /**
   * Get video posts from users in the specified word group (public endpoint)
   */
  async getWordGroupVideos({
    id,
    limit = 50,
    offset = 0,
  }: {
    id: number
    limit?: number
    offset?: number
  }): Promise<WordGroupVideosResponse> {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    })
    const url = `${this.baseUrl}/api/word-groups/${id}/videos?${params.toString()}`
    return this.fetch(url)
  }

  // Public Feeds API methods

  /**
   * Get all public feeds (public endpoint)
   */
  async getPublicFeeds(): Promise<PublicFeed[]> {
    const url = `${this.baseUrl}/api/public/feeds`
    const response = await this.fetch<PublicFeedsResponse>(url)
    return response.feeds
  }

  /**
   * Get video posts from a public feed (public endpoint)
   */
  async getPublicFeedVideos({
    slug,
    limit = 50,
    offset = 0,
  }: {
    slug: string
    limit?: number
    offset?: number
  }): Promise<PublicFeedVideosResponse> {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    })
    const url = `${this.baseUrl}/api/public/feeds/${slug}/videos?${params.toString()}`
    return this.fetch(url)
  }

  /**
   * Internal fetch method with error handling
   */
  private async fetch<T>(endpoint: string): Promise<T> {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(
        `Word Groups API error (${response.status}): ${errorText || response.statusText}`,
      )
    }

    return response.json()
  }
}

// Singleton instance
export const wordGroupsApiClient = new WordGroupsApiClient()
